import React, { useEffect, useState } from 'react';
import PageHeader from '../components/common/PageHeader';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Grid,
  Skeleton,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  LocalShipping,
  Business,
  Co2,
  People,
  LocationOn,
  DirectionsBoat,
  East,
  Straighten,
  Scale,
} from '@mui/icons-material';
import DashboardNotifications from '../components/notifications/DashboardNotifications';
import { dashboardApi } from '../services/dashboardApi';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import { companyApi } from '../services/api/companyApi';

interface MetricValue {
  value: number;
  growth: number;
}

interface StatDetails {
  total: number;
  ytd: MetricValue;
  mtd: MetricValue;
}

interface EmissionsStats {
  total: {
    current: number;
    ytd: MetricValue;
    mtd: MetricValue;
  };
  ene: {
    current: number;
    ytd: MetricValue;
    mtd: MetricValue;
  };
  ops: {
    current: number;
    ytd: MetricValue;
    mtd: MetricValue;
  };
  ei: {
    current: number;
    ytd: MetricValue;
    mtd: MetricValue;
  };
}

interface SummaryData {
  transactions: StatDetails;
  licenses: StatDetails;
  emissions: EmissionsStats;
  users: StatDetails;
  geolocations: StatDetails;
  shipments: StatDetails;
  distance: StatDetails;
  weight: StatDetails;
}

interface StatCardProps {
  title: string;
  subtitle?: string;
  data?: StatDetails | EmissionsStats;
  icon: React.ReactNode;
  accentColor: string;
  loading?: boolean;
  /** If provided, a "View details" link is rendered at the bottom of the card */
  redirectPath?: string;
  /** Custom value formatter — overrides the default formatValue for StatDetails cards */
  valueFn?: (val: number) => string;
}

// Pill-shaped trend badge
const TrendBadge: React.FC<{ value: number }> = ({ value }) => {
  const theme = useTheme();
  const isUp = value >= 0;
  const color = isUp ? theme.palette.success.main : theme.palette.error.main;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.3,
        px: 1,
        py: 0.25,
        borderRadius: '20px',
        background: alpha(color, 0.1),
        border: `1px solid ${alpha(color, 0.2)}`,
      }}
    >
      {isUp
        ? <TrendingUp sx={{ fontSize: 12, color }} />
        : <TrendingDown sx={{ fontSize: 12, color }} />
      }
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color, lineHeight: 1 }}>
        {isUp ? '+' : ''}{value}%
      </Typography>
    </Box>
  );
};

const formatCount = (val: number) => {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
  return val.toLocaleString();
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  subtitle,
  data,
  icon,
  accentColor,
  loading,
  redirectPath,
  valueFn,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const formatValue = (val: number, isEi = false) => {
    if (isEi) return val.toFixed(2);
    if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
    if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
    return val.toLocaleString();
  };

  // Emissions values come in kg — convert to tons
  const formatEmissionValue = (val: number, isEi = false) => {
    if (isEi) return val.toFixed(2);
    const tons = val / 1000;
    if (tons >= 1_000_000) return `${(tons / 1_000_000).toFixed(1)}M`;
    if (tons >= 1_000) return `${(tons / 1_000).toFixed(1)}K`;
    return tons.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const isEmissions = (d: any): d is EmissionsStats =>
    !!(d?.ene && d.total && typeof d.total === 'object');
  const isStatDetails = (d: any): d is StatDetails =>
    !!(d?.ytd && d.mtd);

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${alpha(accentColor, 0.14)}`,
        borderRadius: 3,
        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        // Accent top bar
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${accentColor}, ${alpha(accentColor, 0.5)})`,
        },
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: `0 16px 40px ${alpha(accentColor, 0.18)}, 0 4px 12px ${alpha(accentColor, 0.1)}`,
          border: `1px solid ${alpha(accentColor, 0.3)}`,
          background: 'rgba(255, 255, 255, 0.98)',
        },
      }}
    >
      <CardContent sx={{ p: 3, '&:last-child': { pb: 0 }, flex: 1 }}>

        {/* Header row: icon badge + label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${alpha(accentColor, 0.15)}, ${alpha(accentColor, 0.06)})`,
              color: accentColor,
              flexShrink: 0,
              '& svg': { fontSize: '1.35rem' },
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: '0.7rem', color: alpha(theme.palette.text.secondary, 0.65), lineHeight: 1.3 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Main content */}
        {loading ? (
          <Box>
            {/* Mirrors the YTD + TOT row layout (label + value on the left, trend pill on the right) */}
            {[0, 1].map((i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: i === 0 ? 1 : 0,
                }}
              >
                <Box>
                  <Skeleton variant="text" width={36} sx={{ fontSize: '0.68rem' }} />
                  <Skeleton variant="text" width={80} sx={{ fontSize: '0.95rem' }} />
                </Box>
                <Skeleton variant="rounded" width={52} height={20} sx={{ borderRadius: '20px' }} />
              </Box>
            ))}
          </Box>
        ) : (
          <Box>
            {!!isStatDetails(data) && (
              <>
                {/* All-time total */}
                {/* <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '2rem',
                      color: 'text.primary',
                      lineHeight: 1,
                      letterSpacing: '-0.03em',
                    }}
                  >
                    {formatValue(data.total)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.4, fontWeight: 500 }}>
                    All-time total
                  </Typography>
                </Box>

                <Divider sx={{ mb: 1.5, borderColor: alpha(accentColor, 0.1) }} /> */}

                {/* YTD row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      YTD
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                      {(valueFn ?? formatValue)(data.ytd.value)}
                    </Typography>
                  </Box>
                  <TrendBadge value={data.ytd.growth} />
                </Box>

                {/* TOT row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      TOT
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                      {(valueFn ?? formatValue)(data.total)}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {!!isEmissions(data) && (
              <>
                {/* All-time total */}
                {/* <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '2rem',
                      color: 'text.primary',
                      lineHeight: 1,
                      letterSpacing: '-0.03em',
                    }}
                  >
                    {formatValue(data.total.current)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.4, fontWeight: 500 }}>
                    All-time CO₂e (kg)
                  </Typography>
                </Box>

                <Divider sx={{ mb: 1.5, borderColor: alpha(accentColor, 0.1) }} /> */}

                {/* Emissions breakdown rows */}
                {[
                  { label: 'TOTAL (YTD)', details: data.total.ytd },
                  { label: 'ENE (YTD)', details: data.ene.ytd },
                  { label: 'OPS (YTD)', details: data.ops.ytd },
                  { label: 'EI (YTD)', details: data.ei.ytd, isEi: true },
                ].map((m, idx) => (
                  <Box
                    key={m.label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: idx < 3 ? 0.75 : 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {m.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>
                        {formatEmissionValue(m.details.value, m.isEi)}
                      </Typography>
                    </Box>
                    <TrendBadge value={m.details.growth} />
                  </Box>
                ))}
              </>
            )}
          </Box>
        )}
      </CardContent>

      {/* ── View Details footer link ── */}
      {redirectPath && !loading && (
        <Box
          onClick={() => navigate(redirectPath)}
          sx={{
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            borderTop: `1px solid ${alpha(accentColor, 0.1)}`,
            transition: 'background 0.2s',
            '&:hover': {
              background: alpha(accentColor, 0.05),
              '& .view-arrow': { transform: 'translateX(3px)' },
            },
          }}
        >
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: accentColor,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            View details
          </Typography>
          <East
            className="view-arrow"
            sx={{
              fontSize: '0.85rem',
              color: accentColor,
              transition: 'transform 0.2s ease',
            }}
          />
        </Box>
      )}
    </Card>
  );
};

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SummaryData | null>(null);
  const [companyName, setCompanyName] = useState<string>('');

  const isLicenseeAdmin = user?.accountType === AccountType.LicenseeAdmin;
  const isSubCompanyAdmin = user?.accountType === AccountType.SubCompanyAdmin;

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        // For LicenseeAdmin, scope summary data to their own company
        const filters = isLicenseeAdmin && user?.companyId
          ? { companyId: user.companyId }
          : {};

        const response = await dashboardApi.getSummaryCards(filters);
        if (response.success) {
          setData(response.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard summary:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [isLicenseeAdmin, isSubCompanyAdmin, user?.companyId]);

  // Fetch company name for LicenseeAdmin
  useEffect(() => {
    const fetchCompanyName = async () => {
      if (user?.companyId) {
        try {
          const company = await companyApi.getById(user.companyId);
          setCompanyName(company.name);
        } catch (err) {
          console.error('Error fetching company name:', err);
          setCompanyName('');
        }
      }
    };
    fetchCompanyName();
  }, [user?.companyId]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fafbfc 0%, #f4f6f8 40%, #e8f4f2 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(144, 191, 69, 0.04) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(108, 118, 124, 0.03) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Page Header with Licensee info for LicenseeAdmin */}
        <PageHeader
          description={`Track shipments, emissions, and key carbon footprint metrics at a glance for Licensee: ${companyName} (ID: ${user?.companyId})`}
        />
      

      {/* ── Summary Cards ── */}
      <Grid container spacing={3} sx={{ mb: 6, position: 'relative', zIndex: 1 }}>
        {(() => {
          if (isSubCompanyAdmin) {
            // SubCompanyAdmin: Shipments, Emissions, Distance, Weight
            return (
          <>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Shipments"
                subtitle="Shipments processed"
                data={data?.shipments}
                loading={loading}
                icon={<LocalShipping />}
                accentColor={theme.palette.primary.main}
                redirectPath="/analytics"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Emissions"
                subtitle="CO₂ equivalent (T)"
                data={data?.emissions}
                loading={loading}
                icon={<Co2 />}
                accentColor={theme.palette.warning.main}
                redirectPath="/ghg-reporting/company"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Distance"
                subtitle="Total distance (km)"
                data={data?.distance}
                loading={loading}
                icon={<Straighten />}
                accentColor="#7c3aed"
                redirectPath="/analytics"
                valueFn={(val) => val.toLocaleString()}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Weight"
                subtitle="Total transported weight (T)"
                data={data?.weight}
                loading={loading}
                icon={<Scale />}
                accentColor={theme.palette.info.main}
                redirectPath="/analytics"
                valueFn={(val) => (val / 1000).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              />
            </Grid>
          </>
            );
          }
          if (isLicenseeAdmin) {
            // LicenseeAdmin: Transactions, Geolocation, Emissions, Shipments (own company)
            return (
          <>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Transactions"
                subtitle="Shipment records tracked"
                data={data?.transactions}
                loading={loading}
                icon={<LocalShipping />}
                accentColor={theme.palette.primary.main}
                redirectPath="/statistics/company"
                valueFn={formatCount}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Geolocation"
                subtitle="Location lookups performed"
                data={data?.geolocations}
                loading={loading}
                icon={<LocationOn />}
                accentColor="#7c3aed"
                redirectPath="/statistics/company"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Emissions"
                subtitle="CO₂ equivalent (T)"
                data={data?.emissions}
                loading={loading}
                icon={<Co2 />}
                accentColor={theme.palette.warning.main}
                redirectPath="/ghg-reporting/company"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Shipments"
                subtitle="Unique shipments processed"
                data={data?.shipments}
                loading={loading}
                icon={<DirectionsBoat />}
                accentColor={theme.palette.info.main}
                redirectPath="/statistics/company"
              />
            </Grid>
          </>
            );
          }
          // MasterAdmin / CarbonCareAdmin: Licensees, Users, Transactions, Emissions
          return (
          <>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Licensees"
                subtitle="Active licensees"
                data={data?.licenses}
                loading={loading}
                icon={<Business />}
                accentColor={theme.palette.success.main}
                redirectPath="/companies"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Users"
                subtitle="Registered accounts"
                data={data?.users}
                loading={loading}
                icon={<People />}
                accentColor={theme.palette.info.main}
                redirectPath="/users"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Transactions"
                subtitle="Shipment records tracked"
                data={data?.transactions}
                loading={loading}
                icon={<LocalShipping />}
                accentColor={theme.palette.primary.main}
                redirectPath="/statistics/overview"
                valueFn={formatCount}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Emissions"
                subtitle="CO₂ equivalent (T)"
                data={data?.emissions}
                loading={loading}
                icon={<Co2 />}
                accentColor={theme.palette.warning.main}
                redirectPath="/ghg-reporting/overview"
              />
            </Grid>
          </>
          );
        })()}
      </Grid>

      <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
        <Grid item xs={12}>
          <Card sx={{ height: '430px', background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(20px) saturate(180%)', border: `1px solid rgba(0, 0, 0, 0.08)`, borderRadius: 3, transition: 'all 0.3s ease-in-out', display: 'flex', flexDirection: 'column', '&:hover': { boxShadow: `0 16px 32px ${theme.palette.primary.main}15`, transform: 'translateY(-4px)' } }}>
            <CardContent sx={{ p: 4, '&:last-child': { pb: 4 }, height: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <DashboardNotifications />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;